import { getFramework, IFramework } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import { WsTimer } from './timer';
import { IWsMsg, IWsOption, WsType } from './type';

export class WsClient {
    /** 配置 */
    private option: IWsOption;
    /** ws 实例 */
    private ws: WebSocket | null;
    /** 服务端ws key */
    private wsMemberKey: string;
    /** 心跳定时器 */
    private timer: WsTimer | null;
    /** 实例事件前缀 */
    private instanceEvent = 'wsc';
    /** 关闭后是否需要重连 */
    private closeReconnect: boolean;
    /** 来自服务端断开 */
    private closeFromServer: boolean;
    /** 框架核心 */
    private framework: IFramework;

    private readonly closeEvent = 'close';
    private readonly editEvent = 'edit';
    private readonly readyEvent = 'ready';
    private readonly heartbeatEvent = 'heartbeat';
    private readonly membersChangeEvent = 'members';

    constructor(identity: string, option: IWsOption) {
        this.framework = getFramework(identity);
        this.option = option;
        this.ws = null;
        this.timer = null;
        this.closeReconnect = true;
        this.closeFromServer = false;
        this.wsMemberKey = '';
    }

    /**
     * 是否支持 websocket
     */
    static get isSupper() {
        return !!window.WebSocket;
    }

    /**
     * 基础信息
     */
    get info() {
        return {
            wsMemberKey: this.wsMemberKey,
            uid: this.option.uid,
            group: this.option.group,
        };
    }

    /**
     * 获取事件名称
     * @param event
     * @returns
     */
    private getEventName(eventName: string) {
        return `${this.instanceEvent}_${eventName}`;
    }

    /** 释放资源 */
    private dispose() {
        this.timer?.stopLoop();
        this.timer?.destroy();
        this.ws = null;
        this.timer = null;
        // 关闭链接
        this.framework.events.emit(this.getEventName(this.closeEvent), this.closeReconnect, this.closeFromServer);
    }

    /**
     * 发送消息
     * @param data
     * @returns
     */
    private sendData(data: IWsMsg) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN && this.wsMemberKey) {
            this.ws.send(JSON.stringify(data));
            return true;
        }
        return false;
    }

    /**
     * 发送编辑操作
     * @param content 业务内容
     * @param ext 额外内容
     * @param type 消息类型
     */
    async send(content: any[], type = WsType.Edit) {
        let data = JSON.stringify(content);
        // 找出json字符串中，值是md5格式的值，通过base64Kit.getRemoteUrlAsync(md5)获取远程地址，替换回原字符串中
        // (?!.*:.*,) 是负向前瞻，确保匹配的不是一个key
        const md5Regex = /:\s*"([a-fA-F0-9]{32})"/g;
        const matches = data.matchAll(md5Regex);
        for (const match of matches) {
            const md5Value = match[1];
            try {
                const remoteUrl = await base64Kit.getRemoteUrlAsync(md5Value);
                if (remoteUrl) {
                    data = data.replace(`:"${md5Value}"`, `:"${remoteUrl}"`);
                }
            } catch (error) {
                console.error(`Failed to convert MD5 ${md5Value} to remote URL:`, error);
            }
        }
        this.sendData({
            type,
            content: data,
            ws_member_key: this.wsMemberKey,
            group: this.option.group,
        });
    }

    /**
     * 关闭链接
     */
    close() {
        this.closeReconnect = false;
        this.closeFromServer = false;
        this.ws?.close();
    }

    /**
     * 创建链接
     */
    connect(group?: string | number, uid?: string | number) {
        if (this.ws) return;
        // 设置参数
        const groupID = group || this.option.group;
        const userID = uid || this.option.uid;
        this.option.group = groupID;
        this.option.uid = userID;
        if (!groupID || !userID) {
            console.log(`创建ws失败，group，uid 参数不可为空`);
            return;
        }
        // 清除定时器
        this.timer?.stopLoop();
        this.wsMemberKey = '';
        this.closeReconnect = true;
        this.closeFromServer = false;
        // pre 环境可以使用 uid , 不需要 token
        const wsHost = `${this.option.host}?group=${groupID}${
            this.option.is_debug ? `&uid=${userID}&is_debug=1` : ''
        }&access_token=${encodeURIComponent(this.option.token)}`;
        this.ws = new WebSocket(wsHost);
        console.log('ws connect 链接... ', wsHost);
        this.ws.onopen = (ev) => {
            // 发送创建链接
            this.ws?.send(JSON.stringify({ type: WsType.Connect, group: groupID }));
            // 开启心跳包发送
            this.timer = new WsTimer('heartbeat');
            // let i = 1;
            this.timer.startLoop(() => {
                // console.log(
                //     'ws 发送心跳',
                //     i++,
                //     {
                //         type: WsType.Heartbeat,
                //         ws_member_key: this.wsMemberKey
                //     },
                //     new Date().toLocaleTimeString()
                // );
                this.sendData({
                    type: WsType.Heartbeat,
                    ws_member_key: this.wsMemberKey,
                });
            }, this.option.heartbeat || 10000);
            console.log('ws open 打开', ev);
        };
        this.ws.onclose = (ev) => {
            console.log('ws onclose 关闭', ev);
            this.dispose();
        };
        this.ws.onerror = (ev) => {
            console.log('ws onerror 错误', ev);
            // this.dispose();
        };

        this.ws.onmessage = (ev) => {
            try {
                const data: IWsMsg = JSON.parse(ev.data);
                // 操作响应
                if (data.type === WsType.Edit) {
                    try {
                        if (data.content) {
                            this.framework.events.emit(
                                this.getEventName(this.editEvent),
                                JSON.parse(data.content),
                                data.from_ws_member_key,
                            );
                        }
                    } catch (e) {
                        console.log('ws message 解析错误', e, data.content);
                    }
                    return;
                }
                // 服务端主动断开
                if (data.type === WsType.Close) {
                    console.log('ws 服务端断开', data);
                    this.closeReconnect = false;
                    this.closeFromServer = true;
                    return;
                }
                // 建立链接
                if (data.type === WsType.Connect) {
                    console.log('ws 建立链接', data);
                    // 设置唯一标记
                    this.wsMemberKey = data.ws_member_key;
                    this.framework.events.emit(this.getEventName(this.readyEvent), this.wsMemberKey);
                    return;
                }
                // 成员变更
                if (data.type === WsType.MembersChange) {
                    console.log('ws 成员变更', data);
                    // 触发变更事件
                    this.framework.events.emit(this.getEventName(this.membersChangeEvent));
                    return;
                }
                // 心跳包
                if (data.type === WsType.Heartbeat) {
                    // console.log('ws 收到心跳', data);
                    this.framework.events.emit(this.getEventName(this.heartbeatEvent));
                    return;
                }
            } catch (e) {
                console.log('ws message 解析错误', e);
            }
        };
    }

    /**
     * 关闭链接
     */
    onClose(cb: (closeReconnect: boolean, closeFromServer: boolean) => void) {
        return this.framework.events.onUnique(this.getEventName(this.closeEvent), cb);
    }

    /**
     * 编辑广播响应
     */
    onEdit(cb: (content: any[], from_ws_member_key: string) => void) {
        return this.framework.events.onUnique(this.getEventName(this.editEvent), cb);
    }

    /**
     * 心跳响应
     */
    onHeartbeat(cb: () => void) {
        return this.framework.events.onUnique(this.getEventName(this.heartbeatEvent), cb);
    }

    /**
     * 准备就绪
     */
    onReady(cb: (ws_member_key: string) => void) {
        return this.framework.events.onUnique(this.getEventName(this.readyEvent), cb);
    }

    /**
     * 成员变更
     */
    onMembersChange(cb: () => void) {
        return this.framework.events.onUnique(this.getEventName(this.membersChangeEvent), cb);
    }
}
